import { ApiError } from '@/lib/api';

/**
 * Turns a raw API error into a single human-readable sentence.
 *
 * Handles the two shapes the backend can return:
 *   1. FastAPI request validation — `{ detail: [{ type, loc, msg, ctx }, …] }`
 *      (an array of per-field errors). Rendered verbatim this looks like a
 *      JSON blob; we map each item to plain language.
 *   2. Domain errors — `{ error, detail: "<string>" }` (see core/errors.py).
 *
 * Falls back to a friendly generic message when the error is unrecognised.
 */

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Notes',
  contract_type: 'Type',
  vendor_name: 'Vendor',
  customer_name: 'Customer',
  contract_value: 'Value',
  currency: 'Currency',
  start_date: 'Start date',
  end_date: 'End date',
  auto_renewal: 'Auto-renewal',
  notice_period_days: 'Notice period',
  status: 'Status',
};

interface PydanticError {
  type?: string;
  loc?: unknown;
  msg?: string;
  ctx?: Record<string, unknown>;
}

function fieldKey(loc: unknown): string {
  return Array.isArray(loc) && loc.length > 0 ? String(loc[loc.length - 1]) : '';
}

function sentenceCase(field: string): string {
  const s = field.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function labelFor(loc: unknown): string {
  const key = fieldKey(loc);
  return FIELD_LABELS[key] ?? (key ? sentenceCase(key) : 'This field');
}

function humanizeItem(item: PydanticError): string {
  const label = labelFor(item.loc);
  const type = item.type ?? '';

  if (type.startsWith('date')) return `${label} must be a valid date.`;
  if (type === 'missing') return `${label} is required.`;
  if (type === 'string_too_long') {
    const max = typeof item.ctx?.max_length === 'number' ? ` (max ${item.ctx.max_length} characters)` : '';
    return `${label} is too long${max}.`;
  }
  if (fieldKey(item.loc) === 'contract_value') return `${label} must be a valid number.`;

  const msg = (item.msg ?? '').replace(/^Value error,\s*/i, '').trim();
  // Model-level errors (e.g. end-before-start) have no field in `loc` — show
  // the message on its own rather than prefixing it with "This field:".
  if (!fieldKey(item.loc)) return msg || 'Invalid input.';
  return msg ? `${label}: ${msg}` : `${label} is invalid.`;
}

function humanizeDetailString(detail: string): string {
  const lower = detail.toLowerCase();
  if (
    lower.includes('duplicate key') ||
    lower.includes('already exists') ||
    lower.includes('unique constraint')
  ) {
    return 'A contract with this name already exists. Please choose a different name.';
  }

  // Replace raw snake_case field names with their friendly labels.
  let out = detail;
  for (const [field, label] of Object.entries(FIELD_LABELS)) {
    out = out.split(field).join(label.toLowerCase());
  }
  out = out.charAt(0).toUpperCase() + out.slice(1);
  if (!/[.!?]$/.test(out)) out += '.';
  return out;
}

export function humanizeApiError(
  err: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  if (!(err instanceof ApiError)) return fallback;

  const detail: unknown = err.body?.detail;

  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => (d !== null && typeof d === 'object' ? humanizeItem(d as PydanticError) : ''))
      .filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
  }

  if (typeof detail === 'string' && detail.trim()) {
    return humanizeDetailString(detail);
  }

  switch (err.status) {
    case 403:
      return 'You do not have permission to do this.';
    case 404:
      return 'This item could not be found. It may have been deleted.';
    case 409:
      return 'This change conflicts with the current state. Please refresh and try again.';
    default:
      return fallback;
  }
}
